import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Pagination,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { FaHdd, FaSync, FaDatabase, FaFile, FaSearch, FaTimes } from 'react-icons/fa';
import StorageApi from '../../../api/StorageApi';
import { formatDateTime } from '../../../utils/formatUtils';
import './StorageUsageManager.css';

const RECORDS_PER_PAGE = 20;

function usageHealth(percent, unlimited = false) {
  if (unlimited) {
    return { status: 'healthy', label: 'Unlimited', variant: 'success', tone: 'health-healthy' };
  }
  const value = Number(percent) || 0;
  if (value >= 90) {
    return { status: 'critical', label: 'Critical', variant: 'danger', tone: 'health-critical' };
  }
  if (value >= 75) {
    return { status: 'warning', label: 'Warning', variant: 'warning', tone: 'health-warning' };
  }
  return { status: 'healthy', label: 'Healthy', variant: 'success', tone: 'health-healthy' };
}

function StorageSummaryCard({ title, icon, storage, typeLabel }) {
  const unlimited = storage?.unlimited === true;
  const percent = unlimited ? 0 : Math.min(100, Number(storage?.usage_percent) || 0);
  const health = usageHealth(percent, unlimited);

  return (
    <Card className={`storage-summary-card h-100 border-0 shadow-sm ${health.tone}`}>
      <Card.Body>
        <div className="d-flex align-items-start justify-content-between mb-3">
          <div>
            <div className="storage-summary-label text-muted small">{title}</div>
            <div className="storage-summary-value">{storage?.formatted || '0 B'}</div>
            <div className="text-muted small">
              {unlimited
                ? 'No limit configured'
                : `of ${storage?.limit_formatted || '—'} limit`}
            </div>
          </div>
          <div className="storage-summary-icon">{icon}</div>
        </div>

        <div className="storage-health-header">
          <span className="storage-health-label">Storage health</span>
          <span className={`storage-health-pill storage-health-pill--${health.status}`}>
            <span className="storage-health-dot" aria-hidden="true" />
            {health.label}
          </span>
        </div>

        {!unlimited && (
          <div className="storage-health-bar-track" aria-hidden="true">
            <div
              className={`storage-health-bar-fill storage-health-bar-fill--${health.status}`}
              style={{ width: `${percent}%` }}
            />
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-2 small">
          <span className="storage-health-percent">
            {unlimited ? 'Usage tracking only' : `${percent.toFixed(1)}% used`}
          </span>
          <Badge bg={health.variant} className="storage-type-badge">
            {typeLabel}
          </Badge>
        </div>
      </Card.Body>
    </Card>
  );
}

function ObjectStoragePagination({ page, total, pageSize, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;

  const items = [];
  const maxVisible = 7;
  let start = Math.max(1, page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);

  items.push(
    <Pagination.Prev
      key="prev"
      disabled={page <= 1}
      onClick={() => onPageChange(page - 1)}
    />
  );

  if (start > 1) {
    items.push(
      <Pagination.Item key={1} onClick={() => onPageChange(1)}>
        1
      </Pagination.Item>
    );
    if (start > 2) {
      items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }
  }

  for (let p = start; p <= end; p += 1) {
    items.push(
      <Pagination.Item key={p} active={p === page} onClick={() => onPageChange(p)}>
        {p}
      </Pagination.Item>
    );
  }

  if (end < totalPages) {
    if (end < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
    }
    items.push(
      <Pagination.Item key={totalPages} onClick={() => onPageChange(totalPages)}>
        {totalPages}
      </Pagination.Item>
    );
  }

  items.push(
    <Pagination.Next
      key="next"
      disabled={page >= totalPages}
      onClick={() => onPageChange(page + 1)}
    />
  );

  return <Pagination className="mb-0 justify-content-end">{items}</Pagination>;
}

const StorageUsageManager = () => {
  const api = useMemo(() => new StorageApi(), []);
  const [loading, setLoading] = useState(true);
  const [objectsLoading, setObjectsLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [usage, setUsage] = useState(null);
  const [objects, setObjects] = useState([]);
  const [objectsTotal, setObjectsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.fetchUsage();
      if (res?.data?.success) {
        setUsage(res.data.data);
      } else {
        setError(res?.data?.message || 'Failed to load storage usage');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load storage usage');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadObjects = useCallback(async () => {
    setObjectsLoading(true);
    try {
      const res = await api.fetchObjects({
        page,
        pageSize: RECORDS_PER_PAGE,
        search: debouncedSearch,
      });
      if (res?.data?.success) {
        setObjects(res.data.data?.records || []);
        setObjectsTotal(Number(res.data.data?.total) || 0);
      } else {
        setError(res?.data?.message || 'Failed to load object storage');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load object storage');
    } finally {
      setObjectsLoading(false);
    }
  }, [api, page, debouncedSearch]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  useEffect(() => {
    loadObjects();
  }, [loadObjects]);

  const handleRefresh = async () => {
    await Promise.all([loadUsage(), loadObjects()]);
  };

  const handleReconcile = async () => {
    setReconciling(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.reconcile();
      if (res?.data?.success) {
        if (res.data.data) {
          setUsage(res.data.data);
        }
        await loadObjects();
        setSuccess('Storage usage recalculated successfully.');
      } else {
        setError(res?.data?.message || 'Failed to recalculate storage');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to recalculate storage');
    } finally {
      setReconciling(false);
    }
  };

  const rangeStart = objectsTotal === 0 ? 0 : (page - 1) * RECORDS_PER_PAGE + 1;
  const rangeEnd = Math.min(page * RECORDS_PER_PAGE, objectsTotal);

  return (
    <Container fluid className="storage-usage-page py-3">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">
            <FaHdd className="me-2 text-primary" />
            Storage Usage
          </h4>
          <p className="text-muted mb-0 small">
            Database and file storage for your organization. Data is recalculated in the background;
            use Recalculate for an immediate refresh.
          </p>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={handleRefresh}
            disabled={loading || objectsLoading || reconciling}
          >
            <FaSync className="me-1" />
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={handleReconcile}
            disabled={loading || reconciling}
          >
            {reconciling ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Recalculating…
              </>
            ) : (
              'Recalculate now'
            )}
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {loading && !usage ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Row className="g-3 mb-4">
            <Col md={6}>
              <StorageSummaryCard
                title="Data storage"
                typeLabel="Database"
                icon={<FaDatabase />}
                storage={usage?.data_storage}
              />
            </Col>
            <Col md={6}>
              <StorageSummaryCard
                title="File storage"
                typeLabel="Attachments"
                icon={<FaFile />}
                storage={usage?.file_storage}
              />
            </Col>
          </Row>

          {usage?.last_calculated_at && (
            <p className="text-muted small mb-3">
              Data storage last calculated:{' '}
              <span className="fw-semibold">{formatDateTime(usage.last_calculated_at)}</span>
              {usage?.updated_at && (
                <>
                  {' '}
                  · Last updated: {formatDateTime(usage.updated_at)}
                </>
              )}
            </p>
          )}

          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <span className="fw-semibold">Storage by object</span>
                <div className="storage-objects-search" style={{ minWidth: 260, maxWidth: 360 }}>
                  <InputGroup size="sm">
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="search"
                      placeholder="Search by object label or API name…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label="Search objects"
                    />
                    {search && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setSearch('')}
                        aria-label="Clear search"
                      >
                        <FaTimes />
                      </Button>
                    )}
                  </InputGroup>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {objectsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <Table responsive hover className="mb-0 storage-objects-table">
                  <colgroup>
                    <col className="storage-col-object" />
                    <col className="storage-col-records" />
                    <col className="storage-col-storage" />
                    <col className="storage-col-calculated" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Object</th>
                      <th className="text-end storage-th-records">Records</th>
                      <th className="text-end storage-th-storage">Storage</th>
                      <th className="storage-th-calculated">Last calculated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {objects.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">
                          {debouncedSearch
                            ? 'No objects match your search.'
                            : 'No object storage data yet. Run Recalculate or wait for the background job.'}
                        </td>
                      </tr>
                    ) : (
                      objects.map((row) => (
                        <tr key={row.object_name}>
                          <td className="storage-td-object">
                            <div className="fw-medium">{row.object_label || row.object_name}</div>
                            {row.object_label &&
                              row.object_label !== row.object_name && (
                                <div className="text-muted small">{row.object_name}</div>
                              )}
                          </td>
                          <td className="text-end storage-td-records">
                            {Number(row.record_count || 0).toLocaleString()}
                          </td>
                          <td className="text-end storage-td-storage">
                            <span className="storage-size-value">
                              {row.storage_formatted || '0 B'}
                            </span>
                          </td>
                          <td className="storage-td-calculated">
                            {row.last_calculated_at ? (
                              <span className="storage-calculated-value">
                                {formatDateTime(row.last_calculated_at)}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
            {objectsTotal > 0 && (
              <Card.Footer className="bg-white d-flex flex-wrap align-items-center justify-content-between gap-2">
                <span className="text-muted small">
                  Showing {rangeStart}–{rangeEnd} of {objectsTotal.toLocaleString()} objects
                </span>
                <ObjectStoragePagination
                  page={page}
                  total={objectsTotal}
                  pageSize={RECORDS_PER_PAGE}
                  onPageChange={setPage}
                />
              </Card.Footer>
            )}
          </Card>
        </>
      )}
    </Container>
  );
};

export default StorageUsageManager;
